/*
AI model releases from the radio's releases tracker, for the /calendar/releases/ page.
The radio publishes only models confirmed by an official source (API catalog, OpenRouter,
the studio's blog or X account), each with a type (text, image, video, speech, music).
Studios carry `expected`: one estimate of their next release — a date range, the chance
of a release within 30 days and what it rests on (the studio's release rhythm and the
rumours and teasers about its next model, which weigh more). Dates are UTC calendar
days. Plain data work only.
*/
const CALENDAR_URL = 'https://radio.thehype.news/release-calendar';
const FETCH_TIMEOUT_MS = 5000;
const DAY_MS = 24 * 3600 * 1000;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const FIRST_YEAR = 2000;
const LAST_YEAR = 2100;
const COLOR_RE = /^#[0-9a-f]{6}$/i;
const SIZE_LABEL = /^\d+(\.\d+)?[MB]$/;
// A studio without a tier from the radio sorts after the known ones.
const LOWEST_TIER = 9;

export const TYPES = ['text', 'image', 'video', 'speech', 'music'];

/**
 * Loads the calendar and keeps only well-formed records: a broken field of one studio or
 * model drops that field or record, not the page.
 * @returns {Promise<{studios: Array<Object>, releases: Array<Object>, attribution: Object}>}
 * @throws {Error} when the radio does not answer in time or answers with an error
 */
export async function fetchReleases() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
        const response = await fetch(CALENDAR_URL, { signal: controller.signal });
        if (!response.ok) {
            throw new Error(`release calendar answered ${response.status}`);
        }
        return normalizeCalendar(await response.json());
    } finally {
        clearTimeout(timer);
    }
}

/** The radio's answer with every field checked; see fetchReleases. */
export function normalizeCalendar(calendar) {
    const studios = listOf(calendar?.studios).map(normalizeStudio).filter(Boolean);
    const known = new Set(studios.map((studio) => studio.id));
    const releases = listOf(calendar?.releases).map(normalizeRelease)
        .filter((release) => release && known.has(release.studio));
    return { studios, releases, attribution: normalizeAttribution(calendar?.attribution) };
}

/** Benchmark credits: Epoch AI and the source of each open-leaderboard test, text only. */
function normalizeAttribution(attribution) {
    const sources = isObject(attribution?.benchmark_sources) ? attribution.benchmark_sources : {};
    const byTest = Object.fromEntries(Object.entries(sources)
        .map(([test, source]) => [test, normalizeSource(source)])
        .filter(([, source]) => source));
    return { benchmarks: normalizeSource(attribution?.benchmarks), benchmark_sources: byTest };
}

const SOURCE_UNITS = ['share', 'rating', 'index'];

/**
 * {name, url, license, unit, credit, citation}: unit «rating» — очки арены (LMArena, Design
 * Arena), «index» — очки индекса 0…100 (Artificial Analysis), иначе доля 0…1; credit — короткая
 * подпись источника (у нескольких тестов одного источника она общая), по умолчанию имя теста;
 * citation — полная подпись, которую требует источник (подсказка ссылки).
 */
function normalizeSource(source) {
    if (!isObject(source) || !isText(source.name)) {
        return null;
    }
    return {
        name: source.name,
        url: textOrEmpty(source.url),
        license: textOrEmpty(source.license),
        unit: SOURCE_UNITS.includes(source.unit) ? source.unit : 'share',
        credit: isText(source.credit) ? source.credit : source.name,
        citation: textOrEmpty(source.citation),
    };
}

function normalizeStudio(studio) {
    if (!isObject(studio) || !isText(studio.id) || !isText(studio.name)) {
        return null;
    }
    return {
        id: studio.id,
        name: studio.name,
        tier: Number.isInteger(studio.tier) ? studio.tier : LOWEST_TIER,
        color: COLOR_RE.test(studio.color || '') ? studio.color : null,
        expected: normalizeExpected(studio.expected),
    };
}

function normalizeExpected(expected) {
    if (!isObject(expected) || !isDayKey(expected.start) || !isDayKey(expected.end)
        || expected.end < expected.start) {
        return null;
    }
    return {
        start: expected.start,
        end: expected.end,
        p30: isShare(expected.p30) ? expected.p30 : null,
        habit: normalizeHabit(expected.habit),
        unsure: isShare(expected.unsure) ? expected.unsure : 0,
        signals: listOf(expected.signals).map(normalizeSignal).filter(Boolean),
    };
}

function normalizeHabit(habit) {
    if (!isObject(habit) || !Number.isFinite(habit.typical_days) || !isShare(habit.weight)) {
        return null;
    }
    return {
        typical_days: habit.typical_days,
        since_last_days: numberOrNull(habit.since_last_days),
        weight: habit.weight,
    };
}

/** A rumour or teaser behind the estimate: an unnamed one ("see you next week") has no name. */
function normalizeSignal(signal) {
    if (!isObject(signal) || !isDayKey(signal.window_start) || !isShare(signal.share)) {
        return null;
    }
    return {
        window_start: signal.window_start,
        window_end: isDayKey(signal.window_end) ? signal.window_end : signal.window_start,
        type: isText(signal.type) ? signal.type : 'rumor',
        confidence: isText(signal.confidence) ? signal.confidence : '',
        share: signal.share,
        source_url: isText(signal.source_url) ? signal.source_url : null,
    };
}

const SPEECH_KINDS = ['tts', 'stt', 'voice'];

function normalizeRelease(release) {
    if (!hasReleaseCore(release)) {
        return null;
    }
    return {
        id: String(release.id),
        name: release.name,
        // Official name when the title is the studio's nickname (Nano Banana Pro).
        api_name: textOrEmpty(release.api_name),
        studio: release.studio,
        date: release.date,
        // approx — the lab announced nothing; the date is when the model appeared in a catalog,
        // on Hugging Face or OpenRouter (radio lib/releases_dates).
        approx_date: release.precision === 'approx',
        // repo_created — the date is only when a Hugging Face repo was created, often privately:
        // the public release date is not known (radio lib/releases_dates).
        date_basis: release.date_basis === 'repo_created' ? 'repo_created' : '',
        type: release.type,
        // A speech model: tts (voiceover), stt (recognition) or voice (speech ↔ speech).
        speech_kind: SPEECH_KINDS.includes(release.speech_kind) ? release.speech_kind : '',
        stage: isText(release.stage) ? release.stage : '',
        access: release.access === 'limited' ? 'limited' : null,
        open_weights: typeof release.open_weights === 'boolean' ? release.open_weights : null,
        context: numberOrNull(release.context),
        price_in: numberOrNull(release.price_in),
        price_out: numberOrNull(release.price_out),
        // The studio's own API id, cached-input price, an introductory price and the price
        // after it — from the announcement or the studio docs (radio lib/releases_params).
        model_id: textOrEmpty(release.model_id),
        price_cached_in: numberOrNull(release.price_cached_in),
        price_intro: release.price_intro === true,
        price_after: normalizePriceAfter(release.price_after),
        // Sizes of one announcement, smallest first ("5M", "209M"); empty for a single size.
        sizes: listOf(release.sizes).filter((size) => SIZE_LABEL.test(size)),
        params_sources: listOf(release.params_sources).filter(isLink).map((source) => ({
            kind: linkLabel(source.kind),
            url: source.url,
        })),
        links: listOf(release.links).filter(isLink).map((link) => ({
            kind: link.kind,
            label: linkLabel(isText(link.label) ? link.label : link.kind),
            url: link.url,
        })),
        eci: normalizeScore(release.eci),
        benchmarks: listOf(release.benchmarks).map(normalizeScore).filter((item) => item?.name),
    };
}

/** Id, name, known type, studio and a real date — without them a model is not shown. */
function hasReleaseCore(release) {
    const idOk = isText(release?.id) || Number.isInteger(release?.id);
    return idOk
        && isText(release.name)
        && isText(release.studio)
        && isDayKey(release.date)
        && TYPES.includes(release.type);
}

function normalizePriceAfter(after) {
    if (!isObject(after) || !Number.isFinite(after.in) || !Number.isFinite(after.out)) {
        return null;
    }
    return { in: after.in, out: after.out };
}

function isLink(link) {
    return isObject(link) && isText(link.url) && isText(link.kind);
}

/**
 * {score, rank, total} with finite numbers (and the benchmark name when there is one).
 * A score the studio reported itself (source "reported") may come without a place — the
 * radio ranks it only when enough models report that test; it carries by and url.
 */
function normalizeScore(item) {
    if (!isObject(item) || !Number.isFinite(item.score)) {
        return null;
    }
    const reported = item.source === 'reported';
    const ranked = [item.rank, item.total].every(Number.isFinite);
    if (!ranked && !reported) {
        return null;
    }
    const score = {
        name: textOrEmpty(item.name),
        score: item.score,
        rank: ranked ? item.rank : null,
        total: ranked ? item.total : null,
        best: numberOrNull(item.best),
    };
    return reported ? { ...score, ...reportedBy(item) } : score;
}

/** Who reported the score and where: {source, by, url}, empty text when missing. */
function reportedBy(item) {
    return { source: 'reported', by: textOrEmpty(item.by), url: textOrEmpty(item.url) };
}

function textOrEmpty(value) {
    return isText(value) ? value : '';
}

function listOf(value) {
    return Array.isArray(value) ? value : [];
}

function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value) {
    return typeof value === 'string' && value.length > 0;
}

function isShare(value) {
    return Number.isFinite(value) && value >= 0 && value <= 1;
}

function numberOrNull(value) {
    return Number.isFinite(value) ? value : null;
}

/** A real UTC calendar day YYYY-MM-DD (2026-09-99 is not). */
// Names in the tracker's link labels that are not just a capitalized word.
const LINK_NAMES = {
    'openrouter': 'OpenRouter',
    'hugging face': 'Hugging Face',
    'hf': 'Hugging Face',
    'x': 'X',
};

/** "changelog" → "Changelog", "openrouter" → "OpenRouter": a link label as shown. */
export function linkLabel(text) {
    return LINK_NAMES[text] || capitalize(text);
}

/** "rumor" → "Rumor": a word from the data that starts a label or a line. */
export function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

export function isDayKey(value) {
    const match = DAY_RE.exec(typeof value === 'string' ? value : '');
    if (!match) {
        return false;
    }
    return utcDate(value).toISOString().slice(0, 10) === value;
}

/** A month YYYY-MM between FIRST_YEAR and LAST_YEAR — from the address, so checked. */
export function isMonthKey(value) {
    const match = MONTH_RE.exec(typeof value === 'string' ? value : '');
    return Boolean(match) && Number(match[1]) >= FIRST_YEAR && Number(match[1]) <= LAST_YEAR;
}

/** Today in UTC as YYYY-MM-DD: release dates are UTC days, whatever the reader's zone. */
export function todayKey(now) {
    return now.toISOString().slice(0, 10);
}

/** YYYY-MM of a Date (its UTC day) or of a YYYY-MM-DD key. */
export function monthKey(value) {
    return (typeof value === 'string' ? value : todayKey(value)).slice(0, 7);
}

/** The month `offset` months away, as YYYY-MM. */
export function shiftMonth(key, offset) {
    const [year, month] = key.split('-').map(Number);
    const index = (year * 12) + (month - 1) + offset;
    return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/** "Sep 2026" / "Sep". */
export function monthLabel(key, withYear) {
    const options = withYear ? { month: 'short', year: 'numeric' } : { month: 'short' };
    return formatUtc(`${key}-01`, options);
}

const DATE_NOTES = {
    repo_created: {
        text: 'Hugging Face repo created, release date unknown',
        title: 'Approximate: no verified public release date is available; dated by the creation '
            + 'of its Hugging Face repository, which is often private at first',
    },
};
const APPROX_NOTE = {
    text: '',
    title: 'Approximate: the lab has not announced this model; dated by its first appearance in a catalog',
};

/**
 * What an approximate date rests on: a visible note (empty for a catalog appearance) and a hint.
 * An exact date has neither.
 */
export function dateNote(release) {
    if (!release.approx_date) {
        return { text: '', title: '' };
    }
    return DATE_NOTES[release.date_basis] || APPROX_NOTE;
}

/** "Tue, Sep 22 2026". */
export function longDate(key) {
    const day = formatUtc(key, { weekday: 'short', month: 'short', day: 'numeric' });
    return `${day} ${key.slice(0, 4)}`;
}

/** "Sep 22". */
export function shortDate(key) {
    return formatUtc(key, { month: 'short', day: 'numeric' });
}

function formatUtc(key, options) {
    return utcDate(key).toLocaleDateString('en-US', { ...options, timeZone: 'UTC' });
}

/** The middle day of a range {start, end} (YYYY-MM-DD, UTC); of two middle days, the earlier. */
export function middleDay({ start, end }) {
    const from = utcDate(start);
    const days = Math.round((utcDate(end || start).getTime() - from.getTime()) / DAY_MS);
    return new Date(from.getTime() + (Math.floor(days / 2) * DAY_MS)).toISOString().slice(0, 10);
}

function utcDate(key) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}

/** Whole days from today (UTC) to the key: negative in the past. */
export function daysFromToday(key, now) {
    return Math.round((utcDate(key).getTime() - utcDate(todayKey(now)).getTime()) / DAY_MS);
}

/** "today", "in 16 days", "7 days ago". */
export function relativeDays(key, now) {
    const days = daysFromToday(key, now);
    if (days === 0) {
        return 'today';
    }
    if (days > 0) {
        return `in ${days} day${days === 1 ? '' : 's'}`;
    }
    return `${-days} day${days === -1 ? '' : 's'} ago`;
}

/**
 * The days of the phone days list, newest first: every day with a release of the type from
 * the first month on, and today (with no releases if there were none).
 * @param {Array} releases calendar releases
 * @param {string} type chosen model type
 * @param {{first: string, today: string}} span first month YYYY-MM, today YYYY-MM-DD (UTC)
 * @returns {Array<{day: string, releases: Array}>} day YYYY-MM-DD, its releases by name
 */
export function dayGroups(releases, type, span) {
    const days = new Map([[span.today, []]]);
    releases.forEach((release) => {
        if (release.type !== type || monthKey(release.date) < span.first) {
            return;
        }
        if (!days.has(release.date)) {
            days.set(release.date, []);
        }
        days.get(release.date).push(release);
    });
    return [...days.keys()].sort().reverse().map((day) => ({
        day,
        releases: days.get(day).sort((one, other) => one.name.localeCompare(other.name)),
    }));
}

/** Releases of one type, counted per type for the filter. */
export function countByType(releases) {

    const counts = Object.fromEntries(TYPES.map((type) => [type, 0]));
    releases.forEach((release) => {
        counts[release.type] = (counts[release.type] || 0) + 1;
    });
    return counts;
}

/** Studio letter mark: the first letter or digit of the name. */
export function studioInitial(studio) {
    const match = /[A-Za-z0-9]/.exec(studio.name || studio.id || '');
    return match ? match[0].toUpperCase() : '?';
}

/** "1M", "200K", "32K" tokens. */
export function formatTokens(count) {
    if (!count) {
        return '';
    }
    if (count >= 1000000) {
        return `${Math.round(count / 100000) / 10}M`.replace('.0M', 'M');
    }
    return `${Math.round(count / 1000)}K`;
}

/**
 * "$3 / $15 per 1M tokens" (input / output); an introductory price says so and names the
 * price after it: "$2 / $10 per 1M tokens, introductory, then $4 / $20"; "" when unknown.
 */
export function formatPrice(release) {
    if (release.price_in === null) {
        return '';
    }
    const base = release.price_out === null
        ? `${money(release.price_in)} per 1M input tokens`
        : `${money(release.price_in)} / ${money(release.price_out)} per 1M tokens`;
    if (!release.price_intro) {
        return base;
    }
    const after = release.price_after;
    return after
        ? `${base}, introductory, then ${money(after.in)} / ${money(after.out)}`
        : `${base}, introductory`;
}

/** "$0.2 per 1M tokens" for cached input; "" when unknown. */
export function formatCachedPrice(release) {
    return release.price_cached_in === null ? '' : `${money(release.price_cached_in)} per 1M tokens`;
}

function money(value) {
    return `$${Number(value.toFixed(3))}`;
}
